import React, { useState } from "react"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import { useQueryClient } from "react-query"
import { useConnectedUserNdfDepensesContext } from "../../_PersonnelNdfDepenses"
import { AxiosError } from "axios"
import clsx from "clsx"
import MyAlert from "@common-utils/MyAlert"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR, CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI } from "@common-constants/*"
import { createPersonnelNdfDepenseSimpleRequest } from "../../../core/_requests"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import MyInputFormField from "@common-components/fields/MyInputFormField"

// Sous-composants
import { NdfJustificatifsSection } from "./components/NdfJustificatifsSection"
import { NdfBasicFieldsSection } from "./components/NdfBasicFieldsSection"
import { NdfMealSection } from "./components/NdfMealSection"
import { NdfMileageSection } from "./components/NdfMileageSection"
import { NdfCommentSection } from "./components/NdfCommentSection"
import { NdfSubmitActions } from "./components/NdfSubmitActions"
import { ICreateConnectedUserNdfDepenseSimpleRequestModel } from "../../../../../mes-activites/ndf/core/_models"
import { IHttpErrorResponseModel, IPersonnelNdfDetailModel } from "@common-models/*"
import { toast } from "react-toastify"

export const ConnectedUserNdfDepenseCreateSimple = () => {
     const queryClient = useQueryClient()
     const context = useConnectedUserNdfDepensesContext()
     const [actionAfterSubmitForm, setActionAfterSubmitForm] = useState<"close" | "keepAddingDetails">("close")

     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState({
          distanceEnKmEntreDepartEtDestination: 0,
          distanceTotal: 0,
          montantTTC: 0,
     })

     const validationSchema = Yup.object().shape({
          categorie: Yup.string().label("Catégorie").required("Champ requis"),

          date: Yup.string()
               .label("Date")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.notRequired(),
                    otherwise: schema => schema.required("Champ requis"),
               }),

          montantTTC: Yup.number()
               .label("Montant TTC")
               .when("categorie", {
                    is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),

          moisAnneeKm: Yup.string()
               .label("Mois du trajet")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Veuillez sélectionner le mois et l'année"),
               }),

          joursKm: Yup.array()
               .label("Jours sélectionnés")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema =>
                         schema
                              .min(1, "Veuillez sélectionner au moins un jour dans l'agenda")
                              .test("exact-jours", "Vous devez sélectionner exactement le même nombre de jours que de trajets", function (value) {
                                   const nbTrajets = this.parent.nbTrajets || 0
                                   return value && value.length === nbTrajets
                              }),
               }),

          documents: Yup.array()
               .label("Justificatifs")
               .of(Yup.mixed())
               .when("categorie", {
                    is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema =>
                         schema
                              .of(
                                   Yup.mixed()
                                        .test("fileSize", "Taille <= 10MB", (value: any) => !value || value.size <= 10000000)
                                        .test(
                                             "fileFormat",
                                             "Format JPEG, PNG ou PDF",
                                             (value: any) => !value || ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
                                        )
                              )
                              .min(1, "Au moins un document est requis"),
                    otherwise: schema => schema.notRequired(),
               }),

          repasSeulOuEnGroupe: Yup.string()
               .label("Type de repas")
               .when("categorie", {
                    is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
                    then: schema => schema.required(),
               }),

          invites: Yup.array()
               .label("Invités")
               .when("repasSeulOuEnGroupe", {
                    is: "REPAS_MIDI_EN_GROUPE",
                    then: schema => schema.required(),
               }),

          description: Yup.string()
               .label("Description")
               .when("categorie", {
                    is: (val: string) => val !== CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Champ requis"),
                    otherwise: schema => schema.notRequired(),
               }),

          adresseDepart: Yup.string()
               .label("Adresse de départ")
               .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required() }),
          adresseArrive: Yup.string()
               .label("Adresse d'arrivée")
               .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required() }),
          distanceTotale: Yup.number()
               .label("Distance totale")
               .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required() }),
          nbTrajets: Yup.number()
               .label("Nombre de trajets")
               .when("categorie", { is: CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM, then: s => s.required() }),
          isAllerRetour: Yup.boolean().label("Aller-retour"),
     })

     const initialValues: any = {
          documents: [new File([new Blob()], "")],
          isAllerRetour: false,
          categorie: "",
          moisAnneeKm: "",
          joursKm: [],
     }

     function handleSubmit(values: ICreateConnectedUserNdfDepenseSimpleRequestModel, helpers: FormikHelpers<ICreateConnectedUserNdfDepenseSimpleRequestModel>) {
          helpers.setStatus(null)
          FileAndDriveHelper.processFiles(values.documents!).then(processedFiles => {
               createPersonnelNdfDepenseSimpleRequest(context.ndfId, { ...values, documents: processedFiles })
                    .then(r => {
                         queryClient.setQueryData([context.REACT_QUERY_KEY_GET_LISTING, context.ndfId], (details: IPersonnelNdfDetailModel[] | undefined) => {
                              if (details) {
                                   const dataWithNewDepense = [r.data, ...details]
                                   context.computeTotalExpenses(dataWithNewDepense || [])
                                   return dataWithNewDepense
                              }
                              return []
                         })
                         helpers.setSubmitting(false)
                         if (actionAfterSubmitForm === "close") {
                              toast.success("Votre justificatif a bien été ajouté.")
                              context.setIsAddDepenseModalShown(false)
                              context.setTypeAddDepense(null)
                              context.setIsCardDepenseSimpleCollapsed(false)
                         } else {
                              toast.success("Votre justificatif a bien été ajouté. Vous pouvez continuer...")
                              helpers.resetForm()
                         }
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                         }
                         helpers.setStatus(error?.detail)
                         toast.error(error?.detail, { autoClose: false })
                         helpers.setSubmitting(false)
                    })
          })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
               {helpers => {
                    const isKm = helpers.values.categorie === CONST_PERSONNEL_NDF_DETAILS_CATEGORIE_KM
                    const showCommentAndSubmit = !isKm || (isKm && !!helpers.values.moisAnneeKm)

                    return (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              <div className="row g-9">
                                   {/* COLONNE GAUCHE (ou en-dessous sur mobile) : LE FORMULAIRE DE DONNÉES */}
                                   <div className={clsx("col-lg-7 col-xl-6 order-2 order-lg-1", !helpers.values.categorie && "mx-auto")}>
                                        <div className="d-flex flex-column gap-4">
                                             <NdfBasicFieldsSection helpers={helpers} validationSchema={validationSchema} />

                                             {helpers.values.categorie && (
                                                  <div className="animate__animated animate__fadeIn">
                                                       <div className="my-4 separator separator-dashed" />

                                                       <NdfMealSection helpers={helpers} validationSchema={validationSchema} />

                                                       {!isKm && (
                                                            <MyInputFormField
                                                                 formikProps={helpers}
                                                                 validationSchema={validationSchema}
                                                                 name={"montantTTC"}
                                                                 label={<div className={"required"}>Montant TTC</div>}
                                                                 inputMode={"decimal"}
                                                                 type={"number"}
                                                                 placeholder={"Montant TTC"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                                 classNameLayout={"mb-2"}
                                                            />
                                                       )}

                                                       <NdfMileageSection
                                                            helpers={helpers}
                                                            validationSchema={validationSchema}
                                                            detailsIndemniteKm={detailsIndemniteKm}
                                                            setDetailsIndemniteKm={setDetailsIndemniteKm}
                                                       />

                                                       {showCommentAndSubmit && (
                                                            <div className="animate__animated animate__fadeIn">
                                                                 <NdfCommentSection helpers={helpers} validationSchema={validationSchema} />
                                                                 <div className="separator separator-dashed my-6" />
                                                                 <NdfSubmitActions helpers={helpers} setSubmitAction={setActionAfterSubmitForm} />
                                                            </div>
                                                       )}
                                                  </div>
                                             )}
                                        </div>
                                   </div>

                                   {/* COLONNE DROITE (ou au-dessus sur mobile) : LES JUSTIFICATIFS */}
                                   {helpers.values.categorie && (
                                        <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0 animate__animated animate__fadeInRight">
                                             {/* Position sticky maintenue pour l'écran desktop */}
                                             <div className="position-sticky" style={{ top: "25px", zIndex: 1 }}>
                                                  <NdfJustificatifsSection helpers={helpers} />
                                             </div>
                                        </div>
                                   )}
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}
